<template>
  <v-container id="dashboard" fluid tag="section" class="dashboard-container">
    <!-- ส่วนหัว -->
    <div class="d-flex flex-wrap align-center mb-4">
      <v-icon color="primary" large class="mr-2">mdi-view-dashboard-variant</v-icon>
      <div>
        <h2 class="dash-title">รายงานผลสรุป</h2>
        <div class="caption grey--text text--darken-1">
          <span v-if="summary.generated_at">ข้อมูล ณ {{ thaiDateTime(summary.generated_at) }}</span>
          <v-chip
            x-small
            :color="summary.is_superadmin ? 'deep-purple' : 'primary'"
            dark
            class="ml-2"
          >
            {{ summary.is_superadmin ? "SuperAdmin : ทั้งระบบ" : "Admin ตามภารกิจ" }}
          </v-chip>
        </div>
      </div>
      <v-spacer />
      <v-btn color="primary" outlined :loading="loading" @click="refreshData">
        <v-icon left>mdi-refresh</v-icon> โหลดใหม่
      </v-btn>
    </div>

    <v-alert v-if="error" type="warning" text dense>{{ error }}</v-alert>

    <!-- คำร้องค้างดำเนินการ -->
    <v-alert
      v-if="pendingTotal > 0"
      type="warning"
      text
      border="left"
      icon="mdi-file-alert-outline"
      class="mb-4"
    >
      <div class="d-flex flex-wrap align-center">
        <span>
          มีคำร้องขอยกเลิก/แก้ไขรอดำเนินการ <b>{{ pendingTotal }}</b> รายการ
          ({{ pendingTypes }} ประเภท) ในภารกิจของคุณ
        </span>
        <v-spacer />
        <v-btn small color="warning" depressed to="/admin/home_menu">
          ไปที่ศูนย์แจ้งเตือน <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-alert>

    <!-- 1) ภาพรวมบุคลากร -->
    <div class="section-label">ภาพรวมบุคลากร (ข้าราชการครูและบุคลากรทางการศึกษา)</div>
    <v-row dense>
      <v-col v-for="k in personnelKpis" :key="k.label" cols="6" sm="4" md="3" lg="3">
        <v-card class="kpi-card" outlined>
          <v-skeleton-loader v-if="!loaded" type="list-item-two-line" />
          <div v-else class="d-flex align-center pa-3">
            <v-avatar size="42" :color="k.color" class="mr-3">
              <v-icon dark>{{ k.icon }}</v-icon>
            </v-avatar>
            <div>
              <div class="kpi-value">{{ fmt(k.value) }}</div>
              <div class="kpi-label">{{ k.label }}</div>
              <div v-if="k.sub" class="kpi-sub">{{ k.sub }}</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense class="mt-1">
      <!-- วิทยฐานะ -->
      <v-col cols="12" md="7">
        <v-card outlined class="panel-card fill-height">
          <v-card-title class="panel-title">
            <v-icon left color="indigo">mdi-stairs-up</v-icon> วิทยฐานะ ข้าราชการครู (สายการสอน)
          </v-card-title>
          <v-card-text>
            <div v-for="r in rangRows" :key="r.label" class="bar-row">
              <div class="bar-label">{{ r.label }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: r.pct + '%', background: r.color }" />
              </div>
              <div class="bar-value">
                {{ fmt(r.value) }} <span class="grey--text">({{ r.pct.toFixed(1) }}%)</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- เกษียณ + อัตรากำลังสถานศึกษา -->
      <v-col cols="12" md="5">
        <v-card outlined class="panel-card mb-2">
          <v-card-title class="panel-title">
            <v-icon left color="brown">mdi-account-clock</v-icon> เกษียณอายุราชการ
          </v-card-title>
          <v-card-text>
            <v-row dense class="text-center">
              <v-col cols="6">
                <div class="kpi-value brown--text">{{ fmt(summary.retire.this_year) }}</div>
                <div class="kpi-label">ปีงบประมาณ {{ summary.retire.fiscal_year || "-" }}</div>
              </v-col>
              <v-col cols="6">
                <div class="kpi-value brown--text text--lighten-1">{{ fmt(summary.retire.next_year) }}</div>
                <div class="kpi-label">
                  ปีงบประมาณ {{ summary.retire.fiscal_year ? summary.retire.fiscal_year + 1 : "-" }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card outlined class="panel-card">
          <v-card-title class="panel-title">
            <v-icon left color="teal">mdi-office-building-cog</v-icon> อัตรากำลังสถานศึกษา
          </v-card-title>
          <v-card-text>
            <div class="d-flex justify-space-between mb-1">
              <span>บุคลากรในระบบอัตรากำลัง</span>
              <b>{{ fmt(summary.colleges.etc_total) }} คน</b>
            </div>
            <div class="d-flex justify-space-between mb-1">
              <span>สถานศึกษาที่มีข้อมูลแล้ว</span>
              <b>{{ fmt(summary.colleges.etc_colleges) }} / {{ fmt(summary.colleges.total) }} แห่ง</b>
            </div>
            <v-progress-linear
              :value="summary.colleges.etc_colleges_percent || 0"
              height="10"
              rounded
              color="teal"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 2) การย้าย ตามกำหนดการปัจจุบัน (เฉพาะภารกิจที่ได้รับมอบหมาย) -->
    <template v-if="transferCards.length">
      <div class="section-label mt-4">การย้าย — กำหนดการปัจจุบัน</div>
      <v-row dense>
        <v-col v-for="t in transferCards" :key="t.key" cols="12" md="6">
          <v-card outlined class="panel-card fill-height">
            <v-card-title class="panel-title">
              <v-icon left :color="t.color">{{ t.icon }}</v-icon>
              {{ t.title }}
              <v-spacer />
              <v-chip x-small dark :color="t.period.enabled ? 'green' : 'grey'" class="mr-1">
                {{ t.period.enabled ? "เปิดรับคำร้อง" : "ปิดรับคำร้อง" }}
              </v-chip>
              <v-chip v-if="t.period.process" x-small outlined color="primary">ประมวลผลได้</v-chip>
            </v-card-title>
            <v-card-subtitle class="pb-2">
              รอบ {{ t.period.times }}/{{ t.period.year }} ·
              {{ thaiDate(t.period.start) }} – {{ thaiDate(t.period.stop) }}
            </v-card-subtitle>
            <v-card-text>
              <v-row dense class="text-center">
                <v-col v-for="s in t.stats" :key="s.label" :cols="12 / t.stats.length">
                  <div class="stat-value" :class="s.cls">{{ fmt(s.value) }}</div>
                  <div class="kpi-label">{{ s.label }}</div>
                </v-col>
              </v-row>
              <div class="mt-3">
                <div class="d-flex justify-space-between caption mb-1">
                  <span>{{ t.progressLabel }}</span>
                  <span>{{ fmt(t.progressValue) }} / {{ fmt(t.progressBase) }} ({{ t.progressPct }}%)</span>
                </div>
                <v-progress-linear :value="t.progressPct" height="8" rounded :color="t.color" />
              </div>
              <div v-if="t.extra" class="d-flex justify-space-between caption mt-2">
                <span>{{ t.extra.label }}</span>
                <span>{{ t.extra.text }}</span>
              </div>
            </v-card-text>
            <v-card-actions class="pt-0">
              <v-spacer />
              <v-btn small text color="primary" :to="t.route">
                ไปหน้าจัดการ <v-icon right small>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- 3) ครูผู้ช่วย -->
    <template v-if="summary.assistant">
      <div class="section-label mt-4">รายงานตัวครูผู้ช่วย ปี {{ summary.assistant.year }}</div>
      <v-card outlined class="panel-card">
        <v-card-text>
          <div class="stack-bar mb-3">
            <div
              v-for="s in assistantSegments"
              :key="s.label"
              class="stack-seg"
              :style="{ width: s.pct + '%', background: s.hex }"
              :title="`${s.label}: ${s.value}`"
            />
          </div>
          <v-row dense>
            <v-col v-for="s in assistantSegments" :key="s.label" cols="6" md="3">
              <div class="d-flex align-center">
                <span class="legend-dot" :style="{ background: s.hex }" />
                <span class="kpi-label mr-1">{{ s.label }}</span>
                <b>{{ fmt(s.value) }}</b>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn small text color="primary" to="/admin/assistant_teacher_report/manage_assistant_CollegeReport">
            ไปหน้าจัดการ <v-icon right small>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>

    <!-- 4) รายงานเชิงลึก — mount เฉพาะแท็บที่เปิด เพื่อไม่ให้โหลดข้อมูลหนักทุกชุดพร้อมกัน -->
    <div class="section-label mt-4">รายงานเชิงลึก</div>
    <v-card outlined class="panel-card">
      <v-tabs v-model="detailTab" show-arrows color="primary">
        <v-tab v-for="t in detailTabs" :key="t.key">
          <v-icon left small>{{ t.icon }}</v-icon>{{ t.label }}
        </v-tab>
      </v-tabs>
      <v-divider />
      <div class="pa-2">
        <component :is="activeDetail.component" v-if="activeDetail" :key="activeDetail.key" />
      </div>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import personnel_temporary_notifyVue from "../../component/personnel_temporary_notify.vue";
import personnel_etcSummary from "../../component/personnel_etcSummary.vue";
import personnel_etcSummarySubject from "../../component/personnel_etcSummarySubject.vue";
import personnel_etcSummaryBranch from "../../component/personnel_etcSummaryBranch.vue";
import temporarycollegeemreg from "../../component/temporarycollegeemreg.vue";
import temporaryemployees from "../../component/temporaryemployees";

const DETAIL_TABS = [
  { key: "notify", label: "แจ้งปรับปรุงข้อมูลบุคลากร", icon: "mdi-clipboard-text", component: personnel_temporary_notifyVue },
  { key: "etc_subject", label: "สถิติตามสาขาวิชา", icon: "mdi-chart-bar", component: personnel_etcSummarySubject },
  { key: "etc_type", label: "อัตรากำลังตามประเภทวิชา", icon: "mdi-chart-pie", component: personnel_etcSummary },
  { key: "etc_branch", label: "สาขาที่มีบุคลากรมากที่สุด", icon: "mdi-source-branch", component: personnel_etcSummaryBranch },
  { key: "recruit", label: "ประกาศคัดเลือก/แข่งขัน", icon: "mdi-bullhorn", component: temporarycollegeemreg },
  { key: "applicants", label: "ผู้สมัครตามประกาศ", icon: "mdi-account-multiple-check", component: temporaryemployees }
];

export default {
  name: "DashboardDashboard",

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      loaded: false,
      error: "",
      detailTabs: DETAIL_TABS,
      detailTab: 0,
      notice: { summary: {} },
      summary: {
        personnel: {},
        rang: {},
        retire: {},
        colleges: {},
        transfers: {},
        assistant: null,
        is_superadmin: false
      },
      snackbar: { show: false, color: "success", text: "" }
    };
  },

  computed: {
    activeDetail() {
      return this.detailTabs[this.detailTab] || null;
    },
    pendingTotal() {
      return (this.notice.summary && this.notice.summary.request_total) || 0;
    },
    pendingTypes() {
      return (this.notice.summary && this.notice.summary.request_types) || 0;
    },
    personnelKpis() {
      const p = this.summary.personnel || {};
      const c = this.summary.colleges || {};
      return [
        { label: "บุคลากรทั้งหมด", value: p.total, icon: "mdi-account-group", color: "blue-grey darken-1" },
        { label: "ผู้อำนวยการ", value: p.director, icon: "mdi-account-tie", color: "indigo" },
        { label: "รองผู้อำนวยการ", value: p.se_director, icon: "mdi-account-supervisor", color: "indigo lighten-1" },
        { label: "ครู", value: p.teacher, icon: "mdi-human-male-board", color: "blue darken-1" },
        { label: "ครูผู้ช่วย", value: p.assistant, icon: "mdi-school", color: "amber darken-2" },
        { label: "ศึกษานิเทศก์", value: p.supervision, icon: "mdi-account-search", color: "purple lighten-1" },
        {
          label: "บุคลากร 38 ค.(2)",
          value: p.s38,
          icon: "mdi-account-cog",
          color: "cyan darken-2"
        },
        {
          label: "พนักงานราชการ",
          value: p.gov_em,
          icon: "mdi-badge-account",
          color: "teal",
          sub: c.total ? `สถานศึกษา ${this.fmt(c.total)} แห่ง` : ""
        }
      ];
    },
    rangRows() {
      const r = this.summary.rang || {};
      const rows = [
        { label: "ครูผู้ช่วย", value: r.assistant, color: "#ffb300" },
        { label: "คศ.1", value: r.r1, color: "#90caf9" },
        { label: "คศ.2 ชำนาญการ", value: r.r2, color: "#42a5f5" },
        { label: "คศ.3 ชำนาญการพิเศษ", value: r.r3, color: "#1e88e5" },
        { label: "คศ.4 เชี่ยวชาญ", value: r.r4, color: "#1565c0" },
        { label: "คศ.5 เชี่ยวชาญพิเศษ", value: r.r5, color: "#0d47a1" },
        { label: "อื่น ๆ / ไม่ระบุ", value: r.other, color: "#bdbdbd" }
      ].map(x => ({ ...x, value: Number(x.value) || 0 }));
      const total = rows.reduce((a, b) => a + b.value, 0);
      return rows.map(x => ({ ...x, pct: total ? (x.value * 100) / total : 0 }));
    },
    transferCards() {
      const t = this.summary.transfers || {};
      const cards = [];
      if (t.teacher) {
        const c = t.teacher.counts;
        cards.push({
          key: "teacher",
          title: "ย้ายข้าราชการครู (ตำแหน่งครู)",
          icon: "mdi-school",
          color: "primary",
          period: t.teacher.period,
          route: "/admin/transference_personnel",
          stats: [
            { label: "กรณีปกติ", value: c.nm, cls: "info--text" },
            { label: "กรณีพิเศษ", value: c.sp, cls: "error--text" },
            { label: "ย้ายสำเร็จ", value: c.success, cls: "success--text" }
          ],
          progressLabel: "ผู้ยื่นย้ายเทียบกับครูทั้งหมด",
          progressValue: c.saved,
          progressBase: c.base,
          progressPct: c.percent,
          extra:
            c.condition_colleges !== undefined
              ? {
                  label: "สถานศึกษาแจ้งเงื่อนไขรับย้ายแล้ว",
                  text: `${this.fmt(c.condition_colleges)} แห่ง (${c.condition_percent}%)`
                }
              : null
        });
      }
      if (t.govem) {
        const c = t.govem.counts;
        cards.push({
          key: "govem",
          title: "ย้ายพนักงานราชการ",
          icon: "mdi-badge-account",
          color: "teal",
          period: t.govem.period,
          route: "/admin/gov_em/transference_personnel",
          stats: [
            { label: "ยื่นคำร้องแล้ว", value: c.saved, cls: "teal--text" },
            { label: "ยังไม่ยืนยัน", value: c.draft, cls: "grey--text" },
            { label: "ย้ายสำเร็จ", value: c.success, cls: "success--text" }
          ],
          progressLabel: "ผู้ยื่นย้ายเทียบกับพนักงานราชการทั้งหมด",
          progressValue: c.saved,
          progressBase: c.base,
          progressPct: c.percent
        });
      }
      [
        ["director", "ย้ายผู้อำนวยการ", "mdi-account-tie"],
        ["se_director", "ย้ายรองผู้อำนวยการ", "mdi-account-supervisor"]
      ].forEach(([key, title, icon]) => {
        if (!t[key]) return;
        const c = t[key].counts;
        const stats = [
          { label: "ปกติ", value: c.normal, cls: "info--text" },
          { label: "กรณีพิเศษ", value: c.special, cls: "error--text" }
        ];
        if (key === "director") stats.push({ label: "พัฒนาคุณภาพ", value: c.quality, cls: "success--text" });
        cards.push({
          key,
          title,
          icon,
          color: "indigo",
          period: t[key].period,
          route: "/admin/transference_manage_main",
          stats,
          progressLabel: "ผู้ยื่นย้ายเทียบกับจำนวนในตำแหน่ง",
          progressValue: c.total,
          progressBase: c.base,
          progressPct: c.percent,
          extra: c.cancelled ? { label: "ยกเลิกคำร้องแล้ว", text: `${this.fmt(c.cancelled)} ราย` } : null
        });
      });
      return cards;
    },
    assistantSegments() {
      const a = this.summary.assistant || {};
      const total = Number(a.total) || 0;
      return [
        { label: "อนุมัติแล้ว", value: a.approve, hex: "#43a047" },
        { label: "สถานศึกษายืนยันแล้ว", value: a.confirm, hex: "#1e88e5" },
        { label: "ยังไม่รายงาน", value: a.pending, hex: "#bdbdbd" },
        { label: "ขอยกเลิก", value: a.cc, hex: "#e53935" }
      ].map(s => ({ ...s, value: Number(s.value) || 0, pct: total ? ((Number(s.value) || 0) * 100) / total : 0 }));
    }
  },

  watch: {
    detailTab(i) {
      const tab = this.detailTabs[i];
      if (tab && this.$route.query.tab !== tab.key) {
        this.$router.replace({ query: { ...this.$route.query, tab: tab.key } }).catch(() => {});
      }
    }
  },

  created() {
    // รองรับลิงก์จากศูนย์แจ้งเตือน เช่น /admin/Dashboard?tab=notify
    const i = this.detailTabs.findIndex(t => t.key === this.$route.query.tab);
    if (i >= 0) this.detailTab = i;
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      this.error = "";
      try {
        const [sum, notice] = await Promise.all([
          this.$http.post("admin_dashboard_summary.php", { ApiKey: this.ApiKey }),
          this.$http
            .post("admin_notification_center.php", { ApiKey: this.ApiKey })
            .catch(() => ({ data: null }))
        ]);
        if (sum.data && sum.data.status) {
          this.summary = { ...this.summary, ...sum.data };
          this.loaded = true;
        } else {
          this.error = (sum.data && sum.data.message) || "ไม่สามารถโหลดข้อมูลสรุปได้";
        }
        if (notice.data && notice.data.status) this.notice = notice.data;
        return !this.error;
      } catch (e) {
        this.error = "เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง";
        return false;
      } finally {
        this.loading = false;
      }
    },
    async refreshData() {
      const ok = await this.loadData();
      this.snackbar = {
        show: true,
        color: ok ? "success" : "error",
        text: ok ? "ข้อมูลได้รับการอัปเดตแล้ว" : "เกิดข้อผิดพลาดในการอัปเดตข้อมูล"
      };
    },
    fmt(n) {
      const v = Number(n);
      return isNaN(v) ? "-" : v.toLocaleString("th-TH");
    },
    thaiDate(raw) {
      if (!raw) return "-";
      const d = new Date(String(raw).substring(0, 10) + "T00:00:00");
      return isNaN(d) ? raw : d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" });
    },
    thaiDateTime(raw) {
      const d = new Date(String(raw).replace(" ", "T"));
      return isNaN(d)
        ? raw
        : d.toLocaleString("th-TH", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(160deg, #fafafa 0%, #ffffff 50%, #f4f6f8 100%);
  min-height: 100vh;
  font-family: "Sarabun", "Noto Sans Thai", sans-serif;
}
.dash-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #212121;
  line-height: 1.2;
}
.section-label {
  font-weight: 600;
  color: #455a64;
  margin: 8px 0 8px 2px;
  font-size: 0.95rem;
}
.kpi-card,
.panel-card {
  border-radius: 12px !important;
}
.kpi-card {
  transition: box-shadow 0.2s ease;
}
.kpi-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: #263238;
}
.kpi-label {
  font-size: 0.82rem;
  color: #616161;
}
.kpi-sub {
  font-size: 0.72rem;
  color: #9e9e9e;
}
.panel-title {
  font-size: 1rem !important;
  font-weight: 600;
  padding-bottom: 8px;
}
.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
}
.bar-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.bar-label {
  width: 170px;
  flex-shrink: 0;
  font-size: 0.85rem;
  color: #424242;
}
.bar-track {
  flex: 1;
  height: 12px;
  background: #eceff1;
  border-radius: 6px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.6s ease;
}
.bar-value {
  width: 130px;
  text-align: right;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.stack-bar {
  display: flex;
  height: 16px;
  border-radius: 8px;
  overflow: hidden;
  background: #eceff1;
}
.stack-seg {
  height: 100%;
  transition: width 0.6s ease;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}
@media (max-width: 600px) {
  .bar-label {
    width: 110px;
    font-size: 0.75rem;
  }
  .bar-value {
    width: 90px;
    font-size: 0.75rem;
  }
}
</style>
